import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  FormControl,
  InputGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { SelectField } from '@/components/SelectField';
import { ImageUpload } from '@/components/ImageUpload';
import { ModalFormData, modalFormSchema } from './schemas';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { usePostBaladaAppMutation } from '@/services/queries/balada-app';
import { PostBaladaAppRequest } from '@/services/request/balada-app';
import { BaladaAppType } from '@/model';
import { useTranslation } from 'react-i18next';

export interface BaladaAppCreateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
}

export const BaladaAppCreateModal = ({ isOpen, onClose, onDone }: BaladaAppCreateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const postItemMutation = usePostBaladaAppMutation();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm<ModalFormData>({
    defaultValues: {
      title: '',
      identifier: '',
      type: undefined,
      source_url: '',
      file: undefined,
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const payload: PostBaladaAppRequest = {
        title: data.title,
        identifier: data.identifier,
        type: data.type as BaladaAppType,
        source_url: data.source_url,
        image: data.file,
      };

      postItemMutation.mutate(payload, {
        onSuccess: () => {
          onDone?.();
          reset();
          toast({
            status: 'success',
            title: t('pages.application_balada_app.components.balada_app_create_modal.balada_app_create_success'),
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title:
              (resp && resp.message) ||
              t('pages.application_balada_app.components.balada_app_create_modal.balada_app_create_error'),
          });
        },
      });
    },
    [onDone, postItemMutation, reset, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_balada_app.components.balada_app_create_modal.balada_app_create_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <InputField
              withoutFloating
              label={t('pages.application_balada_app.components.balada_app_create_modal.balada_app_create_label_title')}
              {...register('title')}
              maxLength={100}
              rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
            />

            <InputField
              withoutFloating
              label={t('pages.application_balada_app.components.balada_app_create_modal.balada_app_create_label_identifier')}
              {...register('identifier')}
              maxLength={20}
              rootProps={{ isInvalid: !!errors?.identifier, errorMessage: errors?.identifier?.message }}
            />

            <FormControl display='flex' flexDirection='column' gap={2} isInvalid={!!errors?.type}>
              <Text fontWeight='medium'>
                {t('pages.application_balada_app.components.balada_app_create_modal.balada_app_create_label_type')}
              </Text>
              <SelectField {...register('type')}>
                <option value=''>
                  {t('pages.application_balada_app.components.balada_app_create_modal.balada_app_create_type_placeholder')}
                </option>
                <option value={BaladaAppType.OTHERS}>
                  {t('pages.application_balada_app.components.balada_app_create_modal.balada_app_create_type_others')}
                </option>
                <option value={BaladaAppType.TICKETS}>
                  {t('pages.application_balada_app.components.balada_app_create_modal.balada_app_create_type_tickets')}
                </option>
                <option value={BaladaAppType.MEMBERSHIPS}>
                  {t('pages.application_balada_app.components.balada_app_create_modal.balada_app_create_type_memberships')}
                </option>
              </SelectField>
              {errors?.type && (
                <Text color='red.500' fontSize='sm'>
                  {errors.type.message as string}
                </Text>
              )}
            </FormControl>

            <InputField
              withoutFloating
              label={t('pages.application_balada_app.components.balada_app_create_modal.balada_app_create_label_url')}
              helperText={t('pages.application_balada_app.components.balada_app_create_modal.balada_app_create_help_url')}
              {...register('source_url')}
              rootProps={{ isInvalid: !!errors?.source_url, errorMessage: errors?.source_url?.message }}
            />

            <InputGroup display='flex' flexDirection='column' width='100%'>
              <ImageUpload
                label={t('pages.application_balada_app.components.balada_app_create_modal.balada_app_create_label_image')}
                imageURL={''}
                onFileChange={file => setValue('file', file)}
                helpText={t('pages.application_balada_app.components.balada_app_create_modal.balada_app_create_help_text')}
                error={errors?.file?.message?.toString()}
              />
            </InputGroup>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w={'100%'} justifyContent='end' gap={5}>
            <Button variant='outlined' color='gray.600' onClick={handleClose}>
              {t('pages.application_balada_app.components.balada_app_create_modal.balada_app_create_cancel')}
            </Button>
            <Button onClick={handleConfirm} bg='green.500' paddingX={10}>
              {t('pages.application_balada_app.components.balada_app_create_modal.balada_app_create_create')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={postItemMutation.isLoading} />
    </Modal>
  );
};
